import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  referralsData: [],
  selectedReferral: null,
  referralTypes: [],
  referralStatuses: [],
  editedRows: {}, // { referralId: { field: value } }
  loading: false,
  error: null,
  saving: false,
  refreshing: false,
  lastFetched: null,
  detailPanelOpen: false,
  pagination: {
    page: 1,
    page_size: 50,
    total_count: 0,
    total_pages: 0,
  },
  filters: {
    is_active: 'true',
    is_complete: '',
    urgency_level: '',
    search: '',
    assigned_to: '',
    from_date: '',
    to_date: '',
  },
};

export const referralSlice = createSlice({
  name: 'referral',
  initialState,
  reducers: {
    // Set all referrals data
    setReferralsData: (state, action) => {
      state.referralsData = action.payload;
      state.lastFetched = Date.now();
      state.error = null;
    },

    // Set referral types (configuration)
    setReferralTypes: (state, action) => {
      state.referralTypes = action.payload;
    },

    // Set referral statuses (configuration)
    setReferralStatuses: (state, action) => {
      state.referralStatuses = action.payload;
    },

    // Add or update a single referral
    updateReferral: (state, action) => {
      const updatedReferral = action.payload;
      const index = state.referralsData.findIndex(r => r.id === updatedReferral.id);
      if (index !== -1) {
        state.referralsData[index] = { ...state.referralsData[index], ...updatedReferral };
      } else {
        state.referralsData.push(updatedReferral);
      }

      // Also update selectedReferral if it's the same
      if (state.selectedReferral && state.selectedReferral.id === updatedReferral.id) {
        state.selectedReferral = { ...state.selectedReferral, ...updatedReferral };
      }
    },

    // Set selected referral (for detail panel)
    setSelectedReferral: (state, action) => {
      state.selectedReferral = action.payload;
    },

    // Clear selected referral
    clearSelectedReferral: (state) => {
      state.selectedReferral = null;
    },

    // Track cell edit (before saving to API)
    trackCellEdit: (state, action) => {
      const { referralId, field, value } = action.payload;

      if (!state.editedRows[referralId]) {
        state.editedRows[referralId] = {};
      }

      state.editedRows[referralId][field] = value;

      // Update the local data immediately for UI responsiveness
      const index = state.referralsData.findIndex(r => r.id === referralId);
      if (index !== -1) {
        state.referralsData[index] = {
          ...state.referralsData[index],
          [field]: value
        };
      }
    },

    // Clear all edited rows after successful save
    clearEditedRows: (state) => {
      state.editedRows = {};
    },

    // Clear edited rows for a specific referral
    clearEditedRowsForReferral: (state, action) => {
      const referralId = action.payload;
      delete state.editedRows[referralId];
    },

    // Discard edits and revert to original data
    discardEdits: (state, action) => {
      const referralId = action.payload;

      if (referralId) {
        delete state.editedRows[referralId];
      } else {
        state.editedRows = {};
      }
    },

    // Set loading state
    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    // Set refreshing state
    setRefreshing: (state, action) => {
      state.refreshing = action.payload;
    },

    // Set saving state
    setSaving: (state, action) => {
      state.saving = action.payload;
    },

    // Set error
    setError: (state, action) => {
      state.error = action.payload;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Toggle detail panel
    setDetailPanelOpen: (state, action) => {
      state.detailPanelOpen = action.payload;
    },

    // Set pagination
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },

    // Set paginated referrals data with pagination metadata
    setPaginatedReferralsData: (state, action) => {
      const { referrals, pagination } = action.payload;
      state.referralsData = referrals;
      state.pagination = pagination;
      state.lastFetched = Date.now();
      state.error = null;
    },

    // Set filters
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    // Reset entire state
    resetReferrals: () => {
      return initialState;
    },
  },
});

export const {
  setReferralsData,
  setReferralTypes,
  setReferralStatuses,
  updateReferral,
  setSelectedReferral,
  clearSelectedReferral,
  trackCellEdit,
  clearEditedRows,
  clearEditedRowsForReferral,
  discardEdits,
  setLoading,
  setRefreshing,
  setSaving,
  setError,
  clearError,
  setDetailPanelOpen,
  setPagination,
  setPaginatedReferralsData,
  setFilters,
  resetReferrals,
} = referralSlice.actions;

// Selectors with safe defaults
const emptyArray = [];
const emptyObject = {};

export const selectReferralsData = (state) => state.referral?.referralsData || emptyArray;
export const selectSelectedReferral = (state) => state.referral?.selectedReferral || null;
export const selectReferralTypes = (state) => state.referral?.referralTypes || emptyArray;
export const selectReferralStatuses = (state) => state.referral?.referralStatuses || emptyArray;
export const selectEditedRows = (state) => state.referral?.editedRows || emptyObject;
export const selectEditedRowsCount = (state) => Object.keys(state.referral?.editedRows || {}).length;
export const selectLoading = (state) => state.referral?.loading || false;
export const selectRefreshing = (state) => state.referral?.refreshing || false;
export const selectSaving = (state) => state.referral?.saving || false;
export const selectError = (state) => state.referral?.error || null;
export const selectLastFetched = (state) => state.referral?.lastFetched || null;
export const selectDetailPanelOpen = (state) => state.referral?.detailPanelOpen || false;
export const selectPagination = (state) => {
  if (!state.referral?.pagination) {
    return { page: 1, page_size: 50, total_count: 0, total_pages: 0 };
  }
  return state.referral.pagination;
};
export const selectFilters = (state) => state.referral?.filters || initialState.filters;

export default referralSlice.reducer;
